
import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { SEOEnhanced } from '@/components/SEOEnhanced';
import { seoKeywords } from '@/utils/seoEnhanced';

const Contact = () => {
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    toast.success(
      language === 'ar' 
        ? 'تم إرسال رسالتك بنجاح! سنرد عليك قريباً.'
        : 'Your message has been sent successfully! We\'ll get back to you soon.'
    );
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const contactInfo = [
    {
      icon: MessageCircle,
      title: language === 'ar' ? 'واتساب' : 'WhatsApp',
      value: '+20 1012345678',
      description: language === 'ar' ? 'تواصل معنا عبر واتساب' : 'Contact us via WhatsApp',
      action: () => window.open('https://wa.me/201012345678', '_blank')
    },
    {
      icon: MapPin,
      title: language === 'ar' ? 'العنوان' : 'Address',
      value: language === 'ar' ? 'القاهرة، مصر' : 'Cairo, Egypt',
      description: language === 'ar' ? 'مقرنا الرئيسي' : 'Our main office'
    },
    {
      icon: Clock,
      title: language === 'ar' ? 'ساعات العمل' : 'Business Hours',
      value: language === 'ar' ? 'الأحد - الخميس، 9 ص - 6 م' : 'Sun - Thu, 9 AM - 6 PM',
      description: language === 'ar' ? 'توقيت القاهرة' : 'Cairo Time'
    }
  ];

  return (
    <div className={`min-h-screen ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <SEOEnhanced
        title={language === 'ar' 
          ? 'اتصل بمسكن - دعم الإسكان الطلابي في مصر'
          : 'Contact Maskan - Student Housing Support in Egypt'
        }
        description={language === 'ar'
          ? 'تواصل مع فريق دعم مسكن للحصول على مساعدة في العثور على السكن الطلابي المثالي أو للاستفسارات العامة'
          : 'Get in touch with Maskan support team for help finding perfect student accommodation or general inquiries'
        }
        keywords={seoKeywords.contact}
        canonical="/contact"
        structuredDataType="LocalBusiness"
        lang={language}
        breadcrumbs={[
          { name: language === 'ar' ? 'الرئيسية' : 'Home', url: 'https://maskan-student-nest.lovable.app/' },
          { name: language === 'ar' ? 'اتصل بنا' : 'Contact', url: 'https://maskan-student-nest.lovable.app/contact' }
        ]}
      />
      
      <Header />
      
      <main className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            {t('contact_us', 'اتصل بنا', 'Contact Us')}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {language === 'ar' 
              ? 'نحن هنا للمساعدة'
              : 'We\'re Here to Help'
            }
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {language === 'ar'
              ? 'لديك سؤال أو تحتاج مساعدة؟ فريقنا متاح دائماً لمساعدتك في العثور على السكن المثالي.'
              : 'Have a question or need assistance? Our team is always available to help you find the perfect accommodation.'
            }
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle>
                {language === 'ar' ? 'أرسل لنا رسالة' : 'Send us a Message'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    name="name"
                    placeholder={language === 'ar' ? 'الاسم الكامل' : 'Full Name'}
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Input
                    name="email"
                    type="email"
                    placeholder={language === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Input
                    name="subject"
                    placeholder={language === 'ar' ? 'الموضوع' : 'Subject'}
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Textarea
                    name="message"
                    placeholder={language === 'ar' ? 'رسالتك...' : 'Your message...'}
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    required
                  />
                </div>
                <Button type="submit" className="w-full">
                  {language === 'ar' ? 'إرسال الرسالة' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card key={index} className={info.action ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''} onClick={info.action}>
                  <CardContent className="p-6 flex items-start space-x-4 rtl:space-x-reverse">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <IconComponent className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{info.title}</h3>
                      <p className="text-foreground mb-1">{info.value}</p>
                      <p className="text-sm text-muted-foreground">{info.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
